import { supportsTouchInput } from './FocusToggle.js';

export default class SandboxHelp {
    static GUIDANCE_COMPLETE_KEY = 'blickwinkel.sandbox-help.guidance-complete';
    static BREATHING_DELAY_MS = 2000;
    static CARD_INTERACTION_EVENTS = [
        'sandboxCardDragged',
        'flippedCardUp',
        'flippedCardDown',
        'focusedCard',
    ];

    constructor(eventBus) {
        this.eventBus = eventBus;
        this.button = document.getElementById('sandbox-help-toggle');
        this.overlay = document.getElementById('sandbox-help-overlay');
        this.hoverCopy = document.getElementById('sandbox-help-hover-copy');
        this.deckCopy = document.getElementById('sandbox-help-deck-copy');
        this.previousButton = document.getElementById('sandbox-help-previous');
        this.nextButton = document.getElementById('sandbox-help-next');
        this.position = document.getElementById('sandbox-help-position');
        this.step = 0;
        this.progressStep = 0;
        this.visible = false;
        this.breathingTimeout = null;
        this.guidanceComplete = false;

        if (!this.button || !this.overlay || !this.deckCopy) return;

        this.guidanceComplete = this.hasCompletedGuidance();
        this.showLabel = this.overlay.dataset.showLabel;
        this.hideLabel = this.overlay.dataset.hideLabel;
        this.pointerTips = JSON.parse(this.overlay.dataset.pointerTips);
        this.touchTips = JSON.parse(this.overlay.dataset.touchTips);
        this.toggleBound = this.toggle.bind(this);
        this.previousBound = this.showPrevious.bind(this);
        this.nextBound = this.showNext.bind(this);
        this.draggedBound = this.onCardDragged.bind(this);
        this.flippedBound = this.onCardFlipped.bind(this);
        this.resourcesLoadedBound = this.scheduleBreathing.bind(this);
        this.cardInteractionBound = this.completeGuidance.bind(this);

        this.button.addEventListener('click', this.toggleBound);
        this.previousButton?.addEventListener('click', this.previousBound);
        this.nextButton?.addEventListener('click', this.nextBound);
        this.eventBus.on('sandboxCardDragged', this.draggedBound);
        this.eventBus.on('flippedCardUp', this.flippedBound);
        this.eventBus.on('flippedCardDown', this.flippedBound);

        if (!this.guidanceComplete) {
            this.eventBus.once('resourcesLoaded', this.resourcesLoadedBound);
            SandboxHelp.CARD_INTERACTION_EVENTS.forEach((eventName) => {
                this.eventBus.on(eventName, this.cardInteractionBound);
            });
        }

        this.render();
    }

    toggle() {
        this.completeGuidance();
        this.visible = !this.visible;
        this.render();
    }

    hasCompletedGuidance() {
        try {
            return window.localStorage.getItem(SandboxHelp.GUIDANCE_COMPLETE_KEY) === 'true';
        } catch (_error) {
            return false;
        }
    }

    scheduleBreathing() {
        if (this.guidanceComplete || this.breathingTimeout) return;

        this.breathingTimeout = window.setTimeout(() => {
            this.breathingTimeout = null;
            if (!this.guidanceComplete) {
                this.button?.classList.add('sandbox-help-toggle--breathing');
            }
        }, SandboxHelp.BREATHING_DELAY_MS);
    }

    completeGuidance() {
        if (this.guidanceComplete) return;

        this.guidanceComplete = true;
        try {
            window.localStorage.setItem(SandboxHelp.GUIDANCE_COMPLETE_KEY, 'true');
        } catch (_error) {
            // Keep the completion for this page when storage is unavailable.
        }

        this.stopBreathing();
        this.removeGuidanceListeners();
    }

    stopBreathing() {
        if (this.breathingTimeout) {
            window.clearTimeout(this.breathingTimeout);
            this.breathingTimeout = null;
        }
        this.button?.classList.remove('sandbox-help-toggle--breathing');
    }

    removeGuidanceListeners() {
        if (!this.eventBus || !this.cardInteractionBound) return;

        this.eventBus.off('resourcesLoaded', this.resourcesLoadedBound);
        SandboxHelp.CARD_INTERACTION_EVENTS.forEach((eventName) => {
            this.eventBus.off(eventName, this.cardInteractionBound);
        });
    }

    showPrevious() {
        if (this.step === 0) return;
        this.step -= 1;
        this.render();
    }

    showNext() {
        if (this.step >= this.pointerTips.length - 1) return;
        this.step += 1;
        this.render();
    }

    onCardDragged() {
        if (this.progressStep !== 0) return;
        this.progressStep = 1;
        this.step = this.progressStep;
        this.render();
    }

    onCardFlipped() {
        if (this.progressStep < 1 || this.progressStep >= 3) return;
        this.progressStep += 1;
        this.step = this.progressStep;
        this.render();
    }

    render() {
        if (!this.button || !this.overlay || !this.deckCopy) return;

        const toggleCopy = this.visible ? this.hideLabel : this.showLabel;
        const copy = supportsTouchInput() ? this.touchTips : this.pointerTips;

        this.overlay.classList.toggle('hidden', !this.visible);
        this.overlay.setAttribute('aria-hidden', String(!this.visible));
        this.button.setAttribute('aria-pressed', String(this.visible));
        this.button.setAttribute('aria-label', toggleCopy);
        this.button.setAttribute('title', toggleCopy);
        if (this.hoverCopy) this.hoverCopy.textContent = toggleCopy;
        this.deckCopy.textContent = copy[this.step];
        if (this.previousButton) this.previousButton.disabled = this.step === 0;
        if (this.nextButton) this.nextButton.disabled = this.step === copy.length - 1;
        if (this.position) this.position.textContent = `${this.step + 1} / ${copy.length}`;
    }

    destroy() {
        this.stopBreathing();
        this.removeGuidanceListeners();
        if (this.button && this.toggleBound) {
            this.button.removeEventListener('click', this.toggleBound);
        }
        this.previousButton?.removeEventListener('click', this.previousBound);
        this.nextButton?.removeEventListener('click', this.nextBound);
        if (this.eventBus && this.draggedBound) {
            this.eventBus.off('sandboxCardDragged', this.draggedBound);
            this.eventBus.off('flippedCardUp', this.flippedBound);
            this.eventBus.off('flippedCardDown', this.flippedBound);
        }
        this.button = null;
        this.overlay = null;
        this.hoverCopy = null;
        this.deckCopy = null;
        this.previousButton = null;
        this.nextButton = null;
        this.position = null;
        this.resourcesLoadedBound = null;
        this.cardInteractionBound = null;
    }
}
